/* First-touch + last-touch attribution + lightweight funnel event tracking.
   No PII; anonymous client id only. Single attribution architecture (extends Phase 2B). */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AID_KEY = "airo_aid";
const ATTR_KEY = "airo_attr";        // first-touch (immutable once set)
const LAST_KEY = "airo_attr_last";   // last-touch (updated on each campaign/referral visit)

function uid() {
  return "a-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}
export function getAid() {
  try {
    let a = localStorage.getItem(AID_KEY);
    if (!a) { a = uid(); localStorage.setItem(AID_KEY, a); }
    return a;
  } catch { return uid(); }
}

function classify(params, ref) {
  const gclid = params.get("gclid") || params.get("msclkid") || params.get("wbraid");
  const med = (params.get("utm_medium") || "").toLowerCase();
  if (gclid || ["cpc", "ppc", "paid", "paidsearch"].includes(med)) return { channel: "paid" };
  let host = "";
  try { host = ref ? new URL(ref).hostname.replace(/^www\./, "") : ""; } catch { /* ignore */ }
  if (!host) return { channel: "direct" };
  const has = (arr) => arr.some((d) => host.includes(d));
  if (has(["chatgpt.com", "chat.openai.com", "openai.com", "perplexity.ai", "gemini.google", "bard.google", "copilot.microsoft", "bing.com/chat", "claude.ai", "you.com"])) return { channel: "ai" };
  if (has(["google.", "bing.com", "duckduckgo.com", "yahoo.com", "ecosia.org", "brave.com", "baidu.com"])) return { channel: "organic" };
  if (has(["facebook.com", "instagram.com", "twitter.com", "x.com", "t.co", "linkedin.com", "lnkd.in", "youtube.com", "reddit.com", "tiktok.com", "pinterest."])) return { channel: "social" };
  return { channel: "referral" };
}

function hostOf(ref) {
  try { return ref ? new URL(ref).hostname.replace(/^www\./, "") : ""; } catch { return ""; }
}

function buildTouch(params, ref, channel) {
  const externalRef = channel === "organic" || channel === "ai" || channel === "social" || channel === "referral";
  const gclid = params.get("gclid") || params.get("msclkid") || params.get("wbraid") || null;
  return {
    source: params.get("utm_source") || (externalRef ? (hostOf(ref) || channel) : channel),
    medium: params.get("utm_medium") || channel,
    campaign: params.get("utm_campaign") || null,
    term: params.get("utm_term") || null,
    content: params.get("utm_content") || null,
    keyword: params.get("utm_term") || (gclid ? "(paid - not provided)" : null),
    channel,
    landing_page: window.location.pathname,
    referrer: ref || null,
    gclid,
  };
}

// Does this visit carry a real acquisition signal (campaign or external referrer)?
function hasSignal(params, ref, channel) {
  if (params.get("utm_source") || params.get("gclid") || params.get("msclkid") || params.get("wbraid")) return true;
  return channel !== "direct";
}

export function captureAttribution() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = document.referrer || "";
    const { channel } = classify(params, ref);
    const touch = buildTouch(params, ref, channel);
    const nowIso = new Date().toISOString();

    // First-touch: set once, never overwritten.
    let first = JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
    if (!first) {
      first = { ...touch, first_seen: nowIso };
      localStorage.setItem(ATTR_KEY, JSON.stringify(first));
    }

    // Last-touch: update whenever a new campaign/referral visit arrives (last non-direct wins).
    let last = JSON.parse(localStorage.getItem(LAST_KEY) || "null");
    if (!last || hasSignal(params, ref, channel)) {
      last = { ...touch, last_seen: nowIso };
      localStorage.setItem(LAST_KEY, JSON.stringify(last));
    }

    return { ...first, last_touch: last };
  } catch {
    return { channel: "direct", source: "direct", medium: "direct", landing_page: window.location.pathname };
  }
}

export function getAttribution() {
  try {
    const first = JSON.parse(localStorage.getItem(ATTR_KEY) || "null");
    if (!first) return captureAttribution();
    const last = JSON.parse(localStorage.getItem(LAST_KEY) || "null") || first;
    return { ...first, last_touch: last };
  } catch { return { channel: "direct" }; }
}

const sent = new Set();
export function trackEvent(event, meta = {}, { once } = {}) {
  try {
    if (once) {
      const key = `${event}:${once}`;
      if (sent.has(key) || sessionStorage.getItem("ev_" + key)) return;
      sent.add(key); sessionStorage.setItem("ev_" + key, "1");
    }
    const body = JSON.stringify({
      aid: getAid(),
      event,
      path: window.location.pathname,
      service_page: meta.service_page || window.location.pathname,
      attribution: getAttribution(),
      meta,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/analytics/event`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/analytics/event`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
    }
  } catch { /* never block UX */ }
}
